// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import { SCHEDULE_LABELS, type ScheduleLabel } from "@/features/stores/schedule/types";
import { cn } from "@/lib/utils";

/** Tailwind background class per label (tokens defined in globals.css). */
export const SCHEDULE_LABEL_BG: Record<ScheduleLabel, string> = {
  model: "bg-schedule-model",
  off: "bg-schedule-off",
  human: "bg-schedule-human",
  qr: "bg-schedule-qr",
};

interface ScheduleLabelChipProps {
  label: ScheduleLabel;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
}

/** A small label swatch + localized name. Renders as a button when `onClick` is given. */
export function ScheduleLabelChip({ label, selected, onClick, className }: ScheduleLabelChipProps) {
  const { t } = useTranslation("back-office");
  const Comp = onClick ? "button" : "span";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium",
        onClick && "border transition-colors hover:bg-accent",
        selected && "border-primary bg-accent",
        className,
      )}
    >
      <span className={cn("size-2.5 rounded-[3px]", SCHEDULE_LABEL_BG[label])} />
      {t(`stores.schedule.labels.${label}`)}
    </Comp>
  );
}

/** Horizontal legend of all labels. */
export function ScheduleLabelLegend({ className }: { className?: string }) {
  const { t } = useTranslation("back-office");
  return (
    <div className={cn("flex flex-wrap items-center gap-3 text-xs text-muted-foreground", className)}>
      <span>{t("stores.schedule.legend")}</span>
      {SCHEDULE_LABELS.map((label) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <span className={cn("size-2.5 rounded-[3px]", SCHEDULE_LABEL_BG[label])} />
          {t(`stores.schedule.labels.${label}`)}
        </span>
      ))}
    </div>
  );
}
